/**
 * C9's filter controls: who, what, about what, and between when (`MCK-20260521-009`).
 *
 * **The URL is the state.** Nothing here is component state that also happens to be written to
 * the address bar: a control builds the next `ActivityFilters` and navigates, the route parses
 * it back, and the list reads what came out. On an audit log that is worth more than on any
 * other screen — *"here is what happened to Blok B last week"* is a link somebody can paste
 * into a message, and the CSV taken from it is the same question answered as a file.
 *
 * **The bar and the export are one object.** The download button hands `useExportActivity` the
 * same `ActivityFilters` the list is reading, and both go out through `activityQuery()`
 * (`./api.ts`). There is no second notion of "the active filter" for the file to disagree with.
 *
 * **Three sets, each in a `ChipSelect`, and a date pair.** Actor, event and entity are *any-of*
 * — two chips widen the answer rather than narrowing it twice — and the resting *Any …* copy is
 * each picker's own name, so an empty field still reads as a question. The date pair is a day
 * range in the reader's zone; the server's interval is half-open and `./filters.ts` owns that
 * translation.
 *
 * **A cleared field is not a filter for nothing.** `?event_type=` with nothing after it would be
 * *"events whose type is the empty string"* if the server read it that way, which is an empty
 * page on a screen whose empty page means *nothing happened*. `activityQuery()` drops an empty
 * set rather than sending one, and `_filters()` in `domains/activity/router.py` drops a blank
 * value on top of that — two halves, because this is the one wrong answer here that looks right.
 */

import { useCallback } from 'react';

import { Button, ChipSelect, FilterBar, InlineError, Input } from '@/components';
import { membersQueryOptions } from '@/features/people';
import { useMessages } from '@/lib/i18n';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import type { ChipSelectOption, FilterChip } from '@/components';
import type { MessageKeys, Translate } from '@/lib/i18n';

import styles from './Activity.module.css';
import { ActivitySearchBox } from './ActivitySearchBox';
import { ENTITY_TYPES, EVENT_TYPES, eventLabel } from './events';
import {
  dayOfSince,
  dayOfUntil,
  narrowings,
  NO_FILTERS,
  searchFromFilters,
  sinceFromDay,
  untilFromDay,
  withoutNarrowing,
} from './filters';
import type { ActivityFilters, Narrowing } from './filters';
import { activityErrorKey } from './problems';
import { useExportActivity } from './queries';

type Key = MessageKeys['activity'];

/**
 * The entity vocabulary's copy, spelled out rather than interpolated.
 *
 * A computed `` `entity.${value}` `` would type-check against nothing and go missing silently;
 * this map is a compile error the moment a value in `ENTITY_TYPES` has no sentence.
 *
 * TWIN: `ENTITY_TYPES` in `./events.ts`.
 */
const ENTITY_KEY: Readonly<Record<string, Key>> = {
  project: 'entity.project',
  hierarchy_node: 'entity.hierarchy_node',
  drawing_slot: 'entity.drawing_slot',
  version: 'entity.version',
  review: 'entity.review',
  comment: 'entity.comment',
  pin: 'entity.pin',
  project_membership: 'entity.project_membership',
};

/** One entity type, named. An entry with no sentence prints its own name, the fallback an
    unknown event type gets — a filter nobody can read is still a filter that is applied. */
function entityLabel(t: Translate<'activity'>, value: string): string {
  const key = ENTITY_KEY[value];
  return key === undefined ? value : t(key);
}

/**
 * What one set-valued field is holding and what it may still be given, as `ChipSelect` wants
 * them — chips first, then the rest of the vocabulary as the picker's rows.
 *
 * **Both in the vocabulary's own order, never the URL's.** `searchFromFilters` sorts the members
 * before they reach the address bar, so chips read out of the filter object in its order would
 * reshuffle themselves the moment somebody pasted the link back: the same filter, drawn
 * differently.
 */
function chipsOf(
  all: readonly string[],
  members: readonly string[],
  label: (value: string) => string,
): {
  readonly tokens: readonly ChipSelectOption[];
  readonly options: readonly ChipSelectOption[];
} {
  const held = (value: string) => members.includes(value);
  const chip = (value: string) => ({ id: value, label: label(value) });
  return {
    tokens: all.filter(held).map(chip),
    options: all.filter((value) => !held(value)).map(chip),
  };
}

/** The members after a pick, in the vocabulary's order — an unknown value is dropped. */
function withPicked(
  all: readonly string[],
  members: readonly string[],
  picked: string,
): readonly string[] {
  return all.filter((value) => members.includes(value) || value === picked);
}

function without(members: readonly string[], removed: string): readonly string[] {
  return members.filter((value) => value !== removed);
}

/** A filter change is a navigation. The one writer of this screen's search params. */
export function useActivityFilterNavigation(
  projectId: string,
): (next: ActivityFilters, options?: { readonly replace?: boolean }) => void {
  const navigate = useNavigate();
  return useCallback(
    (next: ActivityFilters, options?: { readonly replace?: boolean }) => {
      void navigate({
        to: '/projects/$projectId/activity',
        params: { projectId },
        // A settled search term replaces the entry rather than pushing one (`ActivitySearchBox`):
        // *Back* is the screen somebody came from, not every word they typed.
        replace: options?.replace ?? false,
        // The filter object is rewritten wholesale — that is what makes the URL the state
        // rather than a copy of it — but the phase rides along untouched. It is the chrome's
        // parameter, not this screen's, and clearing a filter must not silently move the whole
        // project's context.
        search: (previous: { phase?: string }) => ({
          ...searchFromFilters(next),
          ...(previous.phase === undefined ? {} : { phase: previous.phase }),
        }),
      });
    },
    [navigate, projectId],
  );
}

export interface ActivityFilterBarProps {
  readonly projectId: string;
  readonly filters: ActivityFilters;
  /** Whether the list behind the bar answered at all — an export of an error is not a file. */
  readonly ready: boolean;
}

export function ActivityFilterBar({ projectId, filters, ready }: ActivityFilterBarProps) {
  const t = useMessages('activity');
  const apply = useActivityFilterNavigation(projectId);
  const exporting = useExportActivity(projectId);
  // Stable between renders, so the box's settle timer is not restarted by a render that did not
  // change the term (a member list landing, an export finishing).
  const settleSearch = useCallback(
    (q: string | null) => {
      apply({ ...filters, q }, { replace: true });
    },
    [apply, filters],
  );
  // Names for the actor picker. `read_people` has an `architect` floor while the activity log
  // is readable a rung lower, so for those roles this call answers `403` and the picker has no
  // names to offer. It renders empty rather than absent: a filter the URL already carries stays
  // applied and stays visible.
  const members = useQuery(membersQueryOptions(projectId));
  const named = members.data?.items ?? [];
  const nameOf = (member: (typeof named)[number]) =>
    member.display_name ?? member.email ?? t('actor.unknown');

  // The actor set's chips, in the member list's order, with the ids this caller cannot name
  // after them — a member list they may not read, or a person who has left. Such an id keeps a
  // chip reading *Someone* and stays removable: the filter is the server's either way, and a
  // control that hid it would lie about what is applied.
  const actorTokens: readonly ChipSelectOption[] = [
    ...named
      .filter((member) => filters.actor.includes(member.user_id))
      .map((member) => ({ id: member.user_id, label: nameOf(member) })),
    ...filters.actor
      .filter((userId) => !named.some((member) => member.user_id === userId))
      .map((userId) => ({ id: userId, label: t('actor.unknown') })),
  ];
  const actorOptions: readonly ChipSelectOption[] = named
    .filter((member) => !filters.actor.includes(member.user_id))
    .map((member) => ({ id: member.user_id, label: nameOf(member) }));

  const events = chipsOf(EVENT_TYPES, filters.eventType, (value) => eventLabel(t, value));
  const entities = chipsOf(ENTITY_TYPES, filters.entityType, (value) => entityLabel(t, value));

  /** One chip per narrowing, in the words the person picked them by. */
  const label = (narrowing: Narrowing): string => {
    const { field, value } = narrowing;
    if (field === 'actor') {
      const member = named.find((one) => one.user_id === value);
      return member === undefined ? t('actor.unknown') : nameOf(member);
    }
    if (field === 'eventType') return eventLabel(t, value);
    if (field === 'entityType') return entityLabel(t, value);
    if (field === 'q') return t('filter.search', { term: value });
    // Prefixed by which bound it is: two chips both reading `30. 9. 2026` would be two filters
    // a person cannot tell apart.
    return `${field === 'since' ? t('filter.since') : t('filter.until')}: ${value}`;
  };

  const summary: readonly FilterChip[] = narrowings(filters).map((narrowing) => ({
    id: `${narrowing.field}:${narrowing.value}`,
    field: narrowing.field,
    label: label(narrowing),
    onRemove: () => {
      apply(withoutNarrowing(filters, narrowing));
    },
  }));

  return (
    <>
      <FilterBar
        legend={t('filter.legend')}
        triggerLabel={t('filter.open')}
        panelLabel={t('filter.legend')}
        closeLabel={t('filter.close')}
        summary={summary}
        clearLabel={t('filter.clear')}
        removeLabel={(name) => t('filter.remove', { name })}
        onClear={() => {
          apply(NO_FILTERS);
        }}
        // The search box sits in the row that never collapses, in the bar's left-hand slot this
        // screen has no saved chips for: a question typed every time does not belong behind a
        // trigger (`KLQ-20260821-003`).
        saved={
          <ActivitySearchBox term={filters.q} onSettle={settleSearch} />
        }
        actions={
          /* The filter object currently on screen, and never a second copy of it. In the bar's
             action slot rather than under the fields, so the one thing this screen is *for* —
             taking the answer away as a file — does not live behind a trigger. */
          <Button
            size="sm"
            loading={exporting.isPending}
            disabled={!ready}
            onClick={() => {
              exporting.mutate(filters);
            }}
          >
            {t('export.action')}
          </Button>
        }
      >
        <div className={[styles.panelField, 'wide'].join(' ')}>
          <span className={styles.panelLabel}>{t('filter.actor')}</span>
          <ChipSelect
            size="sm"
            tokens={actorTokens}
            options={actorOptions}
            addLabel={t('filter.actorAny')}
            removeLabel={(name) => t('filter.remove', { name })}
            onAdd={(userId) => {
              apply({ ...filters, actor: [...filters.actor, userId] });
            }}
            onRemove={(userId) => {
              apply({ ...filters, actor: without(filters.actor, userId) });
            }}
          />
        </div>

        <div className={[styles.panelField, 'wide'].join(' ')}>
          <span className={styles.panelLabel}>{t('filter.event')}</span>
          <ChipSelect
            size="sm"
            tokens={events.tokens}
            options={events.options}
            addLabel={t('filter.eventAny')}
            removeLabel={(name) => t('filter.remove', { name })}
            onAdd={(picked) => {
              apply({
                ...filters,
                eventType: withPicked(EVENT_TYPES, filters.eventType, picked),
              });
            }}
            onRemove={(removed) => {
              apply({ ...filters, eventType: without(filters.eventType, removed) });
            }}
          />
        </div>

        <div className={[styles.panelField, 'wide'].join(' ')}>
          <span className={styles.panelLabel}>{t('filter.entity')}</span>
          <ChipSelect
            size="sm"
            tokens={entities.tokens}
            options={entities.options}
            addLabel={t('filter.entityAny')}
            removeLabel={(name) => t('filter.remove', { name })}
            onAdd={(picked) => {
              apply({
                ...filters,
                entityType: withPicked(ENTITY_TYPES, filters.entityType, picked),
              });
            }}
            onRemove={(removed) => {
              apply({ ...filters, entityType: without(filters.entityType, removed) });
            }}
          />
        </div>

        <div className={styles.panelField}>
          <Input
            type="date"
            size="sm"
            label={t('filter.since')}
            value={dayOfSince(filters.since)}
            onChange={(event) => {
              apply({ ...filters, since: sinceFromDay(event.target.value) });
            }}
          />
        </div>

        <div className={styles.panelField}>
          <Input
            type="date"
            size="sm"
            label={t('filter.until')}
            value={dayOfUntil(filters.until)}
            onChange={(event) => {
              apply({ ...filters, until: untilFromDay(event.target.value) });
            }}
          />
        </div>
      </FilterBar>

      {/* A download that failed has no other surface to fail on: the button returns to rest and
          the file simply never arrives, which reads as nothing having happened. The list's own
          error sentences, from the same map the page uses. */}
      {exporting.isError ? (
        <InlineError variant="block">{t(activityErrorKey(exporting.error))}</InlineError>
      ) : null}
    </>
  );
}
